import React from 'react';
import HelpWalkthrough from '../shared/HelpWalkthrough';
import '../marketing/helpStages.css';

// "How taxes work" walkthrough with animated demo stages, matching the
// Campaigns, Communications and Journeys walkthroughs.

const Tick = ({ size = 9 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="20 6 9 17 4 12" /></svg>
);

/* 1 ── a named rate assembling: badge, name, applies-to chips */
const RateStage = () => (
  <div className="hs-float" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
    <div className="hs-panel hs-rise" style={{ width: 186, display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', animationDelay: '0.1s' }}>
      <span className="hs-pop" style={{ fontSize: 11.5, fontWeight: 800, color: '#007EA0', background: 'rgba(0,126,160,0.1)', borderRadius: 8, padding: '5px 8px', animationDelay: '0.35s' }}>20%</span>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#1A1A1E' }}>VAT</span>
      <span className="hs-pop" style={{ marginLeft: 'auto', width: 24, height: 14, borderRadius: 100, background: '#007EA0', padding: 2, animationDelay: '0.5s' }}>
        <span style={{ display: 'block', width: 10, height: 10, borderRadius: '50%', background: '#fff', marginLeft: 10 }} />
      </span>
    </div>
    <div style={{ display: 'flex', gap: 6 }}>
      <span className="hs-pop hs-chip" style={{ background: '#fff', color: '#007EA0', boxShadow: '0 6px 16px rgba(26,26,30,0.1)', animationDelay: '0.65s' }}><Tick size={8} /> Services</span>
      <span className="hs-pop hs-chip" style={{ background: '#fff', color: '#007EA0', boxShadow: '0 6px 16px rgba(26,26,30,0.1)', animationDelay: '0.8s' }}><Tick size={8} /> Products</span>
      <span className="hs-pop hs-chip" style={{ background: '#fff', color: '#C7C7CC', boxShadow: '0 6px 16px rgba(26,26,30,0.07)', animationDelay: '0.95s' }}>Vouchers</span>
    </div>
  </div>
);

/* 2 ── inclusive maths: a £10.00 price splitting into net + VAT */
const PriceSplitStage = () => (
  <div className="hs-float" style={{ width: 206 }}>
    <div className="hs-rise" style={{ textAlign: 'center', fontSize: 13, fontWeight: 800, color: '#1A1A1E', marginBottom: 8, animationDelay: '0.1s' }}>£10.00</div>
    <div style={{ display: 'flex', width: '100%', height: 26, borderRadius: 8, overflow: 'hidden', boxShadow: '0 10px 24px rgba(26,26,30,0.14)' }}>
      <span className="hs-bar" style={{ flex: 8.33, background: '#29282D', animationDelay: '0.35s' }} />
      <span className="hs-bar" style={{ flex: 1.67, background: '#007EA0', animationDelay: '0.8s' }} />
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7 }}>
      <span className="hs-rise" style={{ fontSize: 10, fontWeight: 700, color: '#4B4B52', animationDelay: '1.0s' }}>£8.33 before tax</span>
      <span className="hs-rise" style={{ fontSize: 10, fontWeight: 700, color: '#007EA0', animationDelay: '1.15s' }}>£1.67 VAT</span>
    </div>
  </div>
);

/* 3 ── per-item overrides inside the editors */
const OverrideStage = () => (
  <div className="hs-float">
    <div className="hs-panel hs-rise" style={{ width: 206, animationDelay: '0.1s' }}>
      <div className="hs-row">
        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#1A1A1E' }}>Balayage</span>
        <span className="hs-pop hs-chip" style={{ marginLeft: 'auto', background: 'rgba(0,126,160,0.1)', color: '#007EA0', animationDelay: '0.45s' }}>VAT 20%</span>
      </div>
      <div className="hs-row">
        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#1A1A1E' }}>Gift voucher</span>
        <span className="hs-pop hs-chip" style={{ marginLeft: 'auto', background: '#F2F2F7', color: '#8E8E93', animationDelay: '0.7s' }}>Tax free</span>
      </div>
    </div>
  </div>
);

/* 4 ── creator scoping: salon rates beside a chair renter's */
const ScopeStage = () => (
  <div className="hs-float" style={{ display: 'flex', gap: 10 }}>
    <div className="hs-panel hs-rise" style={{ width: 104, padding: '12px 12px', animationDelay: '0.15s' }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: '#1A1A1E', marginBottom: 7 }}>The salon</div>
      <span className="hs-pop hs-chip" style={{ background: 'rgba(0,126,160,0.1)', color: '#007EA0', animationDelay: '0.5s' }}>VAT 20%</span>
    </div>
    <div className="hs-panel hs-rise" style={{ width: 104, padding: '12px 12px', animationDelay: '0.35s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
        <span className="hs-avatar" style={{ width: 14, height: 14, background: '#C2557A', opacity: 0.85 }} />
        <span style={{ fontSize: 10.5, fontWeight: 800, color: '#1A1A1E' }}>Maya</span>
      </div>
      <span className="hs-pop hs-chip" style={{ background: '#F2F2F7', color: '#8E8E93', animationDelay: '0.7s' }}>No VAT</span>
    </div>
  </div>
);

const SLIDES = [
  {
    key: 'rates',
    title: 'Name a rate once, it applies everywhere',
    body: 'Create rates like VAT 20% and choose whether they cover services, products or vouchers. Anything new you add to your catalogue picks up the right rates automatically.',
    Stage: RateStage,
  },
  {
    key: 'inclusive',
    title: 'Your prices never change',
    body: 'With inclusive pricing the tax lives inside the price clients already pay. A £10.00 service is £8.33 plus £1.67 VAT, since the 20% is charged on the pre-tax £8.33. Selling in the US? Switch to exclusive and tax is added at checkout instead.',
    Stage: PriceSplitStage,
  },
  {
    key: 'overrides',
    title: 'Fine-tune any single item',
    body: 'Every service, product and voucher can override your defaults from its own editor, like keeping vouchers tax free while everything else carries VAT.',
    Stage: OverrideStage,
  },
  {
    key: 'scope',
    title: 'Your rates are yours alone',
    body: 'Salon rates cover the salon’s items, including work by employed staff. Self-employed stylists manage their own rates from their account, so a chair renter under the VAT threshold can sell tax free in the same salon.',
    Stage: ScopeStage,
  },
];

const TaxesHelp = ({ onClose }) => (
  <HelpWalkthrough eyebrow="How taxes work" slides={SLIDES} onClose={onClose} />
);

export default TaxesHelp;
